import { z } from "zod";
/**
 * Task 9.1 — who else may read this cellar, and how they come to.
 *
 * **An invitation names an address, not a user.** It is the one thing the
 * owner reliably knows about the person they want to show the cellar to, and
 * `User.email` is unique so that it can be looked up. What the answer depends
 * on is whether that address already has an account: if it does, the access
 * row is written on the spot and the person can read the cellar before the
 * form has finished clearing; if it does not, the invitation waits — see the
 * `CellarInvitation` comment in the schema for why waiting is a row.
 *
 * The two cases are one endpoint and two outcomes rather than two endpoints,
 * because the person filling the form does not know which one they are in and
 * should not have to.
 */
/**
 * The same rule the login form uses, folded to lower case for the same
 * reason: an address is an identity here, and `Erik@` and `erik@` are one
 * person. Written out rather than imported from the auth contract, which
 * pairs it with a password and is about a different act.
 */
const emailAddress = z.string().trim().toLowerCase().email();
/**
 * Somebody who can read the cellar today.
 *
 * The name comes with it because the address is how the person was reached and
 * not how they are known: *Erik Lundqvist* is what the owner recognises in a
 * list, and an address alone would make the list unreadable the moment two of
 * them are at the same company.
 */
export const guestSummarySchema = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
    grantedAt: z.string(),
    /**
     * On what footing they reach the cellar. Printed on the row, because with
     * two kinds of access a list that said only *who* would be a list the owner
     * cannot check — and the whole page exists to be checked.
     *
     * Nullish for one release, per CLAUDE.md: a row that says nothing about the
     * role is what an older API sends, and the screen printed the name alone.
     *
     * **That release ended with revision 44 on 2026-09-21 and the leniency came
     * off on the 22nd, taking the nullability with it.** Unlike `awayBy` beside
     * it, null was never a real state here: the query behind this list excludes
     * the owner, so what is left is always one of the two invitable roles. A
     * row with no role was only ever an API too old to have one.
     */
    role: z.enum(["Guest", "Contributor"]),
    /**
     * What this person is shown of a cellar that is not theirs, from 2026-09-22.
     *
     * Two switches rather than one because they answer two questions, and on the
     * row rather than on the cellar because the answer is different for
     * different people — which is the whole of the decision in
     * `project-scope.md`. `seesPrices` was `guestsSeePrices` and applied to
     * everybody; `seesNewWines` is new.
     *
     * **They were nullish for exactly one release, and the null was
     * load-bearing.** It meant *this API had not heard of a setting per
     * person*, which is a different fact from *this person is not shown
     * prices*, and the screen read it as such — falling back to the cellar-wide
     * value that same API was still sending. Without that, the hour between the
     * frontend deploying on a green `main` and `deploy:api` being run by hand
     * would have shown an owner every guest unticked in a cellar that reveals
     * prices to all of them, on the one page whose entire job is being
     * checkable.
     *
     * **That release ended when revision 45 began serving, so both the leniency
     * and the nullability come off together** — unlike every other field in
     * `additive.test.ts`, which keeps its null because the null is a real
     * state. Here it never was: a reader either sees prices or does not, and
     * *nobody said* was a fact about a deploy in progress rather than about a
     * person.
     */
    seesPrices: z.boolean(),
    seesNewWines: z.boolean(),
    /**
     * When this person was last in the cellar at all, from 2026-09-27 — the
     * answer to whether access handed out long ago is still being used. See
     * `project-scope.md`.
     *
     * **Null is a real state and stays after the release**: it means not since
     * the column began being written, which every row that already existed
     * starts as. Nullish rather than nullable for one release, per CLAUDE.md,
     * so a browser in front of an older API reads a row without it as the same
     * *not recorded* it would print anyway.
     */
    lastVisitedAt: z.string().nullish(),
    /**
     * When this person was last in the application at all — added 2026-09-29,
     * **sent to the administrator only**. Every other owner gets null, because
     * what a guest does in other cellars is not theirs to learn; see the two
     * decisions in `project-scope.md`. Null also for an account never seen.
     * Nullish, and it stays so: null is the answer most owners always get.
     */
    lastSeenAt: z.string().nullish(),
});
/**
 * An invitation with nobody behind it yet. No name, because nobody knows it:
 * an address with no account is exactly as much as the cellar has been told.
 */
export const pendingInvitationSchema = z.object({
    id: z.string(),
    email: z.string(),
    invitedAt: z.string(),
});
/**
 * The whole of who reaches this cellar, owner included.
 *
 * The owner is in the response rather than assumed by the screen, so the list
 * reads as the complete answer to *who can see this* — a page that showed only
 * the guests would invite the reader to wonder whether it had left somebody
 * out.
 */
export const cellarGuestsResponseSchema = z.object({
    owner: z.object({ name: z.string(), email: z.string() }),
    guests: z.array(guestSummarySchema),
    pending: z.array(pendingInvitationSchema),
    /**
     * `guestsSeePrices` stood here until 2026-09-22. It was the cellar-wide
     * price setting, and `seesPrices` on each row above is the answer now.
     *
     * It survived one release as the fallback for a browser in front of an API
     * that had not heard of a setting per person, and it leaves with the column
     * it read — `Cellar.guestsSeePrices` — in the same commit, which is the
     * second half of the two-step CLAUDE.md requires for dropping a column.
     */
});
/**
 * Inviting somebody, and on what footing.
 *
 * **The role is a choice from 2026-09-21 and was fixed at `Guest` before it.**
 * There was one kind of person to invite and no reason to ask, and the request
 * said so by not having the field. Now there are two, and the difference
 * between them is what the invited person may do on an evening in somebody
 * else's cellar — read it, or drink in it.
 *
 * **`Owner` is not among them, and that is not an oversight.** Making somebody
 * the owner of a cellar is a transfer: it is offered, it is *accepted*, and it
 * takes the cellar away from whoever offered it. An invitation that could do
 * that quietly, by naming a role, would be the same act without the half that
 * makes it safe. The two are kept apart at the contract rather than by a check
 * further in — see `offerTransferRequestSchema`.
 *
 * Defaulted to `Guest`, which is both the old behaviour and the right default:
 * of the two, it is the one that grants less.
 */
export const inviteGuestRequestSchema = z.object({
    email: emailAddress,
    role: z.enum(["Guest", "Contributor"]).default("Guest"),
});
/**
 * What one named reader is shown of this cellar.
 *
 * **Both switches, sent whole, rather than the one that was tapped.** The
 * thing this request addresses is *what Erik sees*, and that is two booleans —
 * so a body carrying both is the complete new state of it, which is the same
 * reasoning the cellar-wide setting used when it was one. The screen knows
 * both, so sending both costs nothing and a request that can only say this
 * cannot rename a cellar or change a role by accident.
 *
 * The person is in the path and not in the body, for the reason every other
 * write here puts them there: the address of the thing being changed is not
 * part of what it is being changed to.
 */
export const setGuestVisibilityRequestSchema = z.object({
    seesPrices: z.boolean(),
    seesNewWines: z.boolean(),
});
/**
 * `granted` — the address had an account and the cellar is in their list now.
 * `pending` — it had none, and the access is written the day the account is.
 */
export const inviteOutcomeSchema = z.enum(["granted", "pending"]);
/**
 * What came of inviting somebody. The name is null for a pending invitation
 * and present for a granted one, which is what lets the screen say *Erik can
 * read the cellar* in the one case and name only the address in the other.
 */
export const inviteGuestResponseSchema = z.object({
    outcome: inviteOutcomeSchema,
    email: z.string(),
    name: z.string().nullable(),
});
//# sourceMappingURL=sharing.js.map